import React, { useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Wrench, Loader2, CheckCircle2, RefreshCcw } from "lucide-react";

/**
 * 🆕 22/07/2026 — Card di manutenzione admin per allineare il flag legacy `is_paid`
 * su polizze già completamente incassate rata-per-rata.
 * Riporta coerenza a widget/statistiche legacy che leggono `is_paid` invece degli incassi
 * provvigionali granulari (`installment_commission_collections`).
 */
export function AdminSyncIsPaidCard() {
  const [preview, setPreview] = useState(null); // {inspected, eligible, sample}
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [lastResult, setLastResult] = useState(null); // {updated}

  const runPreview = async () => {
    setLoading(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/admin/policies/sync-is-paid?dry_run=true");
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!preview || preview.eligible === 0) return;
    if (!window.confirm(`Confermi di aggiornare il flag "is_paid" su ${preview.eligible} polizze già interamente incassate rata-per-rata?`)) return;
    setApplying(true);
    try {
      const { data } = await api.post("/admin/policies/sync-is-paid?dry_run=false");
      setLastResult(data);
      setPreview(null);
      toast.success(`${data.updated} polizze allineate.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      data-testid="admin-sync-is-paid-card"
      className="bg-gradient-to-br from-indigo-50 via-slate-50 to-white border border-indigo-200 rounded-xl p-4"
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md bg-indigo-600 text-white flex items-center justify-center shrink-0">
            <Wrench className="h-4 w-4" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-indigo-700 font-semibold">Manutenzione dati · Admin</p>
            <h3 className="font-display text-base font-semibold text-slate-900">Allinea flag &ldquo;is_paid&rdquo; con incassi rata-per-rata</h3>
            <p className="text-xs text-slate-600 mt-0.5 max-w-2xl">
              Le polizze già completamente incassate tramite <code className="bg-slate-100 px-1 rounded text-[11px]">installment_commission_collections</code> vengono flaggate come <strong>pagate</strong> per uniformità con i widget legacy (es. &ldquo;Polizze scadute non incassate&rdquo;).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="sync-is-paid-preview-btn"
            variant="outline"
            size="sm"
            onClick={runPreview}
            disabled={loading}
            className="border-indigo-300 text-indigo-800 hover:bg-indigo-50"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-1" />}
            Anteprima
          </Button>
          {preview && preview.eligible > 0 && (
            <Button
              data-testid="sync-is-paid-apply-btn"
              size="sm"
              onClick={apply}
              disabled={applying}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {applying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Applica ({preview.eligible})
            </Button>
          )}
        </div>
      </div>

      {preview && (
        <div className="mt-3 p-3 bg-white border border-indigo-100 rounded-md text-xs space-y-2" data-testid="sync-is-paid-preview">
          <div className="flex items-center gap-4 flex-wrap tabular-nums">
            <span className="text-slate-500">Ispezionate: <strong className="text-slate-900">{preview.inspected}</strong></span>
            <span className="text-slate-500">
              Da allineare: <strong className={preview.eligible > 0 ? "text-indigo-800" : "text-slate-700"}>{preview.eligible}</strong>
            </span>
          </div>
          {preview.eligible === 0 ? (
            <p className="text-emerald-700 text-xs">✓ Tutte le polizze sono già allineate. Nessun intervento necessario.</p>
          ) : (
            <div className="border-t border-indigo-100 pt-2">
              <p className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Anteprima (max 20):</p>
              <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                {preview.sample.map((s) => (
                  <li key={s.id} className="flex items-center gap-3 text-[11px]">
                    <span className="font-mono text-slate-700 w-32 truncate">{s.policy_number || s.id.slice(0, 8)}</span>
                    <span className="text-slate-500 tabular-nums">Dovuto: {formatCurrency(s.total_due)}</span>
                    <span className="text-emerald-700 tabular-nums">Incassato: {formatCurrency(s.collected)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {lastResult && (
        <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded-md text-xs text-emerald-800" data-testid="sync-is-paid-result">
          ✓ Aggiornamento completato: <strong>{lastResult.updated}</strong> polizze allineate su {lastResult.inspected} ispezionate.
        </div>
      )}
    </div>
  );
}
